import * as React from 'react';
import { useState } from 'react';
import { SimpleGrid } from '@chakra-ui/react';
import {
  CardAccordion,
  CardButton,
  ErrorMessage,
  FilterSelector,
  Heading,
  InfoCard,
  PanelShell,
  SuccessMessage,
  Tabs,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { PillToggleGroup } from 'components/ui';
import { Section } from './Section';
import { Tile } from './Tile';
import { ChangeBadge } from './ChangeBadge';
import { ParityBadge } from './ParityBadge';

const FILTER_OPTIONS = [{ label: 'Active' }, { label: 'Archived' }];

// Mirrors the roster's actual split (CareCoordinationSection): "New" is a
// signup-recency signal, not a funnel stage, so it's a separate group that
// ANDs against the funnel pills rather than one more option among them —
// see CareCoordinationStatus's doc comment in models/user.ts.
const NEW_SIGNUP_PILL = [{ value: 'new', label: 'New (last 7 days)' }];
const CARE_COORDINATION_PILLS = [
  { value: 'never_logged_in', label: 'Never Logged In' },
  { value: 'onboarding', label: 'Onboarding' },
];

const VISIT_TIMING_PILLS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'Upcoming This Week' },
  { value: 'none', label: 'None Scheduled' },
];

/**
 * The first interactive pill control in ui/. Chip is a static label and
 * StatusPill is read-only, so filter rows had nothing to reuse — the roster
 * needed the same "tap a few of these to narrow the list" affordance in four
 * separate groups. Chrome comes from `PressablePill` (see Atoms) rather than
 * bespoke styles, so a selected pill reads as the same object as the chips in
 * the rows it filters. This tile is the composition; the pill itself is
 * documented as an atom.
 */
const PillToggleGroupDemo = () => {
  const [isNew, setIsNew] = useState<string[]>(['new']);
  const [statuses, setStatuses] = useState<string[]>(['onboarding']);
  const [timing, setTiming] = useState<string[]>(['today']);

  return (
    <Tile
      label="PillToggleGroup"
      note="multi-select toggles independently; single-select replaces, and clicking the active pill clears it"
      badge={
        <>
          <ChangeBadge kind="new" date="Aug 8, 2026" />
          <ParityBadge name="PillToggleGroup" />
        </>
      }
    >
      <VStack gap={3} align="stretch">
        <PillToggleGroup
          options={NEW_SIGNUP_PILL}
          selected={isNew}
          onChange={setIsNew}
        />
        <PillToggleGroup
          options={CARE_COORDINATION_PILLS}
          selected={statuses}
          onChange={setStatuses}
        />
        <PillToggleGroup
          options={VISIT_TIMING_PILLS}
          selected={timing}
          onChange={setTiming}
          multiple={false}
        />
      </VStack>
    </Tile>
  );
};

const FilterSelectorDemo = () => {
  const [selected, setSelected] = useState(FILTER_OPTIONS[0]);
  return (
    <Tile label="FilterSelector" badge={<ParityBadge name="FilterSelector" />}>
      <FilterSelector
        options={FILTER_OPTIONS}
        selected={selected}
        onChange={setSelected}
      />
    </Tile>
  );
};

const InfoCardDemo = () => (
  <Tile label="InfoCard" badge={<ParityBadge name="InfoCard" />}>
    <InfoCard
      title="Diet"
      subtitle="Updated yesterday"
      headingPill="Care plan"
      titleSize="lg"
      content={<Text>Track allergies, sensitivities, and preferences.</Text>}
      primaryButtonText="Edit"
      onPrimaryButtonClick={() => {}}
    />
  </Tile>
);

const CardAccordionDemo = () => {
  const [expanded, setExpanded] = useState<string | null>('Diet');
  return (
    <Tile label="CardAccordion" badge={<ParityBadge name="CardAccordion" />}>
      <CardAccordion
        items={[
          {
            title: 'Diet',
            summary: <Text my={0}>Tap to expand</Text>,
            content: <Text my={0}>Diet details go here.</Text>,
          },
          {
            title: 'Symptoms',
            canBeChecked: true,
            isChecked: true,
            summary: <Text my={0}>Tap to expand</Text>,
            content: <Text my={0}>Symptom details go here.</Text>,
          },
        ]}
        expandedItem={expanded}
        setExpandedItem={setExpanded}
      />
    </Tile>
  );
};

const CardButtonDemo = () => (
  <Tile label="CardButton" badge={<ParityBadge name="CardButton" />}>
    <VStack gap={2}>
      <CardButton text="Primary" variant="primary" />
      <CardButton text="Secondary" variant="secondary" />
      <CardButton text="Outline" variant="outline" />
    </VStack>
  </Tile>
);

const PanelShellDemo = () => (
  <Tile label="PanelShell" badge={<ParityBadge name="PanelShell" />}>
    <PanelShell maxW={320}>
      <Heading size="sm">Schedule a visit</Heading>
      <Text>Panel content goes here.</Text>
    </PanelShell>
  </Tile>
);

const TabsDemo = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <Tile label="Tabs" badge={<ParityBadge name="Tabs" />}>
      <Tabs
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        tabs={[
          { label: 'Diet', content: <Text>Diet panel</Text> },
          { label: 'Symptoms', content: <Text>Symptoms panel</Text> },
          { label: 'Notes', content: <Text>Notes panel</Text> },
        ]}
      />
    </Tile>
  );
};

const DatePickerReference = () => (
  <Tile
    label="DatePicker"
    note="@ayble-health/dashboard-components"
    badge={<ParityBadge name="DatePicker" />}
  >
    <Text>
      Domain-specific — built on react-day-picker and driven by a live
      host-availability map + timezone from the appointment scheduler. Not shown
      live here; see AppointmentsPage for it in context.
    </Text>
  </Tile>
);

const MessagesDemo = () => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
    <Tile label="ErrorMessage" badge={<ParityBadge name="ErrorMessage" />}>
      <ErrorMessage message="Something went wrong. Please try again." />
    </Tile>
    <Tile label="SuccessMessage" badge={<ParityBadge name="SuccessMessage" />}>
      <SuccessMessage message="Saved!" />
    </Tile>
  </SimpleGrid>
);

export const MoleculeAudit = () => (
  <Section
    id="molecules"
    title="Components — molecules"
    description="Cross-feature composites from @ayble-health/dashboard-components,
      plus careOS-only molecules. Reused across more than one
      feature, or generic enough that they could be."
  >
    <VStack gap={8} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        <FilterSelectorDemo />
        <CardButtonDemo />
        <PillToggleGroupDemo />
      </SimpleGrid>

      <InfoCardDemo />
      <CardAccordionDemo />

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        <PanelShellDemo />
        <TabsDemo />
      </SimpleGrid>

      <DatePickerReference />
      <MessagesDemo />
    </VStack>
  </Section>
);
